import Page from "../../common/page/Page"
import Loader from "../../common/loader/Loader"
import style from "./OrderSummary.module.css"
import { useEffect, useState, useMemo } from "react"
import axios from "axios"
import { useNavigate, useParams } from "react-router-dom"
import { getOrderStatusLabel } from "../../common/utils/StatusConvert"
import { useAuth } from "../../data/useAuth"

const GeneralOrder = () => {
  const { data } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [orders, setOrders] = useState([])
  const [activeTab, setActiveTab] = useState("ALL")
  const navigate = useNavigate()
  const { signOut } = useAuth()

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true)
      try {
        const { data } = await axios.get("/api/orders/user/products")
        setOrders(data || [])
      } catch (err) {
        if (err.response?.status === 401) {
          signOut()
        }
        setError(err.response?.data?.message || err.message || "Failed to load orders")
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [data])

  // Helper to categorize order status string
  const getOrderCategory = (item) => {
    const latestStatusObj = item.order?.order_statuses?.[0]
    const rawStatus = (latestStatusObj?.status || "").toString().toLowerCase()
    const statusInfo = getOrderStatusLabel(latestStatusObj?.status)
    const heading = (statusInfo.heading || "").toLowerCase()

    if (
      rawStatus.includes("failed") ||
      rawStatus.includes("cancel") ||
      heading.includes("failed") ||
      heading.includes("cancel") ||
      rawStatus === "2"
    ) {
      return "CANCELLED"
    }

    return "PLACED"
  }

  // Count items per category
  const tabCounts = useMemo(() => {
    const counts = { ALL: orders.length, PLACED: 0, CANCELLED: 0 }
    orders.forEach((item) => {
      const cat = getOrderCategory(item)
      if (counts[cat] !== undefined) {
        counts[cat]++
      }
    })
    return counts
  }, [orders])

  const filteredOrders = useMemo(() => {
    if (activeTab === "ALL") return orders
    return orders.filter((item) => getOrderCategory(item) === activeTab)
  }, [orders, activeTab])

  return (
    <Page title="My Orders" searchDisabled={true}>
      <div className={style.checkoutContainer}>
        {/* Luxury Hero Banner */}
        <div className={style.ordersHeroCard}>
          <div className={style.ordersHeroText}>
            <h1>My Orders</h1>
            <p>Track, manage, and view detailed receipts for your purchases.</p>
          </div>
        </div>

        {/* Filter Tabs */}
        {!loading && orders.length > 0 && (
          <div className={style.filterTabs}>
            <button
              type="button"
              className={`${style.tabBtn} ${activeTab === "ALL" ? style.activeTabBtn : ""}`}
              onClick={() => setActiveTab("ALL")}
            >
              All Orders ({tabCounts.ALL})
            </button>
            <button
              type="button"
              className={`${style.tabBtn} ${activeTab === "PLACED" ? style.activeTabBtn : ""}`}
              onClick={() => setActiveTab("PLACED")}
            >
              Active / Placed ({tabCounts.PLACED})
            </button>
            <button
              type="button"
              className={`${style.tabBtn} ${activeTab === "CANCELLED" ? style.activeTabBtn : ""}`}
              onClick={() => setActiveTab("CANCELLED")}
            >
              Cancelled / Failed ({tabCounts.CANCELLED})
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ height: "calc(50vh)", display: "flex" }}>
            <div style={{ margin: "auto" }}>
              <Loader />
            </div>
          </div>
        ) : error ? (
          <div className={style.error}>{error}</div>
        ) : filteredOrders.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "48px 20px",
              background: "#ffffff",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="1.5"
              style={{ marginBottom: "12px" }}
            >
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            <h3 style={{ margin: "0 0 6px 0", color: "#1e293b", fontSize: "1.1rem" }}>
              {activeTab === "ALL" ? "No Orders Yet" : "No Orders Found"}
            </h3>
            <p style={{ margin: "0 0 16px 0", color: "#64748b", fontSize: "0.88rem" }}>
              {activeTab === "ALL"
                ? "You haven't placed any orders with us yet."
                : "No orders match the selected status filter."}
            </p>
            <button
              onClick={() => navigate("/")}
              className={style.addAddressBtn}
              style={{ maxWidth: "200px", margin: "0 auto" }}
            >
              Explore Products
            </button>
          </div>
        ) : (
          <div className={style.historyList}>
            {filteredOrders.map((item, index) => {
              const latestStatus = item.order?.order_statuses?.[0]
              const status = getOrderStatusLabel(latestStatus?.status)
              const orderId = item.order?.id

              const qty = parseInt(item.qty, 10) || parseInt(item.Product?.qty, 10) || 1
              const rate = parseFloat(item.amount || item.Product?.rate || 0)
              const gst = parseFloat(item.gst || 0)
              const hasPrice = rate > 0
              const itemTotal = ((rate + gst) * qty).toFixed(2)

              const rawDate = latestStatus?.created_at || item.order?.created_at
              const formattedDate = rawDate
                ? new Date(rawDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : ""

              return (
                <div
                  key={index}
                  className={style.historyCard}
                  onClick={() => navigate(`/orders/${orderId}`)}
                >
                  <div className={style.historyCardHeader}>
                    <div>
                      <span className={style.historyOrderId}>Order #{orderId}</span>
                      {formattedDate && (
                        <div className={style.historyDate}>Placed on {formattedDate}</div>
                      )}
                    </div>
                    <span
                      className={style.statusBadge}
                      style={{
                        backgroundColor: `${status.color}15`,
                        color: status.color,
                        borderColor: `${status.color}30`,
                        fontSize: "0.8rem",
                        padding: "4px 12px",
                      }}
                    >
                      <span
                        className={style.statusDot}
                        style={{ backgroundColor: status.color }}
                      />
                      {status.heading}
                    </span>
                  </div>

                  <div className={style.historyCardBody}>
                    <div className={style.historyItemImg}>
                      <img
                        src={`${import.meta.env.VITE_BACKEND_URL}/public/images/products/${
                          item.Product?.product_images?.[0]?.image_url
                        }`}
                        alt={item.Product?.name || "Order Item"}
                      />
                    </div>

                    <div className={style.historyItemDetails}>
                      <h4 className={style.historyItemTitle}>{item.Product?.name}</h4>
                      <div className={style.historyMetaRow}>
                        <span>Qty: {qty}</span>
                        <span>•</span>
                        <span>₹{rate.toFixed(2)} each</span>
                      </div>
                    </div>

                    <div className={style.historyPriceBox}>
                      {hasPrice && (
                        <span className={style.historyTotalPrice}>₹{itemTotal}</span>
                      )}
                      <span className={style.viewDetailsLink}>
                        View Order →
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Page>
  )
}

export default GeneralOrder
